import Card from "@material-ui/core/Card";
import { makeStyles, Theme } from "@material-ui/core/styles";
import HelpIcon from "@material-ui/icons/Help";
import "bootstrap/dist/css/bootstrap.css";
import { useEffect, useRef } from "react";
import Container from "reactstrap/lib/Container";
import Navbar from "reactstrap/lib/Navbar";
import NavbarBrand from "reactstrap/lib/NavbarBrand";
import {
    changeAppState,
    getAppState,
    useAppState,
} from "../../../../common/appState";
import { appPublicImg } from "../../../../common/consts";
import { sleepAsync } from "../../../../common/functions";
import { useScreenSize } from "../../../../common/hooks/useScreenSize";
import { spaceBetween } from "../../../../common/util/Array/spaceBetween";
import { SuccessButton } from "../../../shared/Button/SuccessButton";
import { WhiteButton } from "../../../shared/Button/WhiteButton";
import { Link } from "../../../shared/Link/LinkWithYouTube";
import { AccountType } from "../../../shared/User/types";
import { zIndex } from "../zIndex";
import { LoginIcon } from "./components/LoginIcon";
import "./style.css";

export default function NavMenu() {
    const ref = useRef<HTMLElement>(null);
    const headerHeight = useHeaderHeight(ref.current);
    const hideAppBar = useHideAppBar();
    const [user] = useAppState("user");

    const [isDarkMode] = useAppState("isDarkMode");
    const c = useNavMenuStyles({
        hideAppBar,
        headerHeight,
        isDarkMode,
    });

    return (
        <>
            <header ref={ref} className={c.header}>
                <Navbar
                    variant="pills"
                    className={spaceBetween(
                        "navbar-inverse navbar-expand-md navbar-toggleable-md border-bottom box-shadow mb-3",
                        c.navBar,
                        "reverseDarkMode"
                    )}
                >
                    <Container>
                        <NavbarBrand tag={Link} to="/">
                            <div className={c.logoImgAndTitle}>
                                <img
                                    className={c.logoImg}
                                    src={`${appPublicImg}logo.png`}
                                />
                                <div className={c.title}>
                                    <span
                                        className={spaceBetween(
                                            "z-apps-title text-light large",
                                            c.titleContainer
                                        )}
                                    >
                                        One World
                                    </span>
                                    <span
                                        className={spaceBetween(
                                            "z-apps-title text-light small",
                                            c.titleContainer
                                        )}
                                    >
                                        Japanese Language Center INC.
                                    </span>
                                </div>
                            </div>
                        </NavbarBrand>
                        <Menus />
                    </Container>

                    {user &&
                        (user.accountType === AccountType.Admin ||
                            user.accountType === AccountType.Teacher) && (
                            <Card
                                className={spaceBetween(c.helpCard)}
                                onClick={() => {
                                    changeAppState("helpPanelState", {
                                        open: true,
                                    });
                                }}
                                elevation={isDarkMode ? 0 : undefined}
                            >
                                {isDarkMode ? (
                                    <WhiteButton
                                        startIcon={<HelpIcon />}
                                        className={c.darkHelpButton}
                                        size="small"
                                    >
                                        Help
                                    </WhiteButton>
                                ) : (
                                    <SuccessButton
                                        startIcon={<HelpIcon />}
                                        className={c.helpButton}
                                        size="small"
                                    >
                                        Help
                                    </SuccessButton>
                                )}
                            </Card>
                        )}
                </Navbar>
            </header>

            <div className={c.spaceBelow} />
        </>
    );
}
const topOverFlow = 50;
const useNavMenuStyles = makeStyles<
    Theme,
    {
        hideAppBar: boolean;
        headerHeight: number;
        isDarkMode: boolean | null;
    }
>(theme => ({
    titleContainer: {
        whiteSpace: "nowrap",
        fontWeight: "bold",
    },
    logoImgAndTitle: {
        display: "flex",
        alignItems: "center",
        gap: 15,
    },
    logoImg: {
        height: 45,
    },
    title: {
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        justifyContent: "center",
        lineHeight: 1.4,
        position: "relative",
        bottom: 2,
    },
    header: ({ hideAppBar }) => ({
        top: hideAppBar ? -(100 + topOverFlow) : -topOverFlow,
        zIndex: zIndex.navMenu.header(false),
    }),
    navBar: ({ isDarkMode }) => ({
        backgroundColor: isDarkMode ? "black" : "#222222",
        marginTop: topOverFlow,
        position: "relative",
    }),
    spaceBelow: ({ headerHeight }) => ({
        height: headerHeight,
        marginBottom: 14,
    }),
    helpCard: ({ isDarkMode }) => ({
        position: "absolute",
        right: 0,
        bottom: -32,
        padding: 2,
        filter: isDarkMode ? "var(--ln-dark-mode)" : undefined,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
    }),
    helpButton: {
        maxHeight: 26,
        minHeight: 26,
        fontSize: "small",
    },
    darkHelpButton: {
        maxHeight: 26,
        minHeight: 26,
        fontSize: "small",
        borderColor: theme.palette.success.dark,
        color: theme.palette.success.dark,
        "&:hover": {
            color: theme.palette.primary.main,
        },
    },
}));

function Menus() {
    const c = useMenusStyles();
    const { screenWidth } = useScreenSize();
    const isHamburger = screenWidth < 768;
    const iconMarginLeft = screenWidth / 30;

    return (
        <>
            <div className={c.iconsArea}>
                {isHamburger && (
                    <LoginIcon
                        containerStyle={{
                            marginLeft: `calc(${iconMarginLeft}px + 0.25rem)`, // Hamburger's right margin is 0.5rem,
                        }}
                        transitionClass={"t500ms"}
                    />
                )}
            </div>
            {!isHamburger && (
                <LoginIcon
                    containerStyle={{
                        marginLeft: Math.min(iconMarginLeft, 25),
                    }}
                    transitionClass={"t1s"}
                />
            )}
        </>
    );
}
const useMenusStyles = makeStyles({
    iconsArea: {
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-end",
    },
});

function useHeaderHeight(headerElement: HTMLElement | null) {
    const [headerHeight, setHeaderHeight] = useAppState("headerHeight");
    const { screenWidth } = useScreenSize();
    useEffect(() => {
        if (headerElement) {
            setHeaderHeight(headerElement.clientHeight - (14 + topOverFlow));
        }
    }, [screenWidth, headerElement]);
    return headerHeight;
}

export function scrollToElement(
    element: HTMLElement | null,
    noSmooth?: boolean,
    headerShown?: boolean
) {
    if (headerShown) {
        const { headerHeight } = getAppState();
        const elementTop = element?.getBoundingClientRect()?.top || 0;

        window.scrollTo({
            top: window.scrollY + elementTop - headerHeight,
            behavior: noSmooth ? undefined : "smooth",
        });
        return;
    }

    hideHeaderTemporarily();
    element?.scrollIntoView(
        noSmooth
            ? true
            : {
                  behavior: "smooth",
              }
    );
}

export let hideHeaderTemporarily: () => void = () => {};
export let showHeader: () => void = () => {};

let previousScrollY = 0;
let isHidden = false;
let appBarTimer = 0;

function useHideAppBar() {
    const [isAppBarHidden, setIsAppBarHidden] = useAppState("isAppBarHidden");

    showHeader = () => {
        setIsAppBarHidden(false);
        isHidden = false;
    };

    hideHeaderTemporarily = () => {
        setIsAppBarHidden(true);
        isHidden = true;

        if (appBarTimer > 0) {
            clearTimeout(appBarTimer);
        }
        appBarTimer = window.setTimeout(() => {
            setIsAppBarHidden(false);
            isHidden = false;
        }, 5000);
    };

    useEffect(() => {
        const scrollHandler = async () => {
            await sleepAsync(10); // In case where starting point's scrollY is 0

            const isRapidScroll = window.scrollY > previousScrollY + 500;
            previousScrollY = window.scrollY;

            if (window.scrollY <= 0) {
                setIsAppBarHidden(false);
                return;
            }

            if (isHidden) {
                return;
            }

            if (!isRapidScroll) {
                return;
            }

            hideHeaderTemporarily();
        };

        window.addEventListener("scroll", scrollHandler);
        return () => {
            window.removeEventListener("scroll", scrollHandler);
        };
    }, []);

    return isAppBarHidden;
}
